import { useLocation } from 'react-router-dom';
import { useAppSelector } from 'src/store/hooks';
import { selectProductHierarchyMinimalData } from 'src/store/selectors/productHierarchySelectors';
import {
  FPNMinimalNode,
  ProductHierarchyMinimalDetails,
  RPNMinimalNode,
} from 'src/types/productLines/productHierarchy.types';

export interface HierarchyContext {
  /** Product Line ID (numeric) - extracted from hierarchy data based on product code in URL */
  productLineId: number | null;
  /** RPN ID (numeric) - extracted from URL (R prefix stripped) */
  rpnId: number | null;
  rpnDisplayId: string | null;
  rpnName: string;
  /** FPN ID (numeric) - extracted from URL (F prefix stripped) */
  fpnId: number | null;
  fpnDisplayId: string | null;
  fpnName: string;
  productLineHierarchyMetadata: ProductHierarchyMinimalDetails | null | undefined;
  rpnHierarchyMetadata: RPNMinimalNode | null | undefined;
  fpnHierarchyMetadata: FPNMinimalNode | null | undefined;
}

/**
 * Hook to get current hierarchy context from URL path
 * Provides selected Product Line, RPN, and FPN data
 *
 * URL Format:
 * - /manage-programs/product-code/:productCode - Product Line page
 * - /manage-programs/product-code/:productCode/rpn/:rpnId - RPN page (rpnId prefixed with 'R')
 * - /manage-programs/product-code/:productCode/rpn/:rpnId/fpn/:fpnId - FPN page (fpnId prefixed with 'F' + 6-digit padding)
 *
 * Examples:
 * - /manage-programs/product-code/ABC123 → productCode='ABC123'
 * - /manage-programs/product-code/ABC123/rpn/R456 → productCode='ABC123', rpnId=456
 * - /manage-programs/product-code/ABC123/rpn/R456/fpn/F010652 → productCode='ABC123', rpnId=456, fpnId=10652
 *
 * Note: This hook now uses the lightweight productHierarchyMinimal data instead of the full hierarchy.
 * The minimal data contains the hierarchy structure with essential fields only, reducing data transfer.
 */
export const useProductRpnFpnHierarchyContext = (): HierarchyContext => {
  const { pathname } = useLocation();
  const productHierarchyData = useAppSelector(selectProductHierarchyMinimalData);

  // Parse values from pathname using new URL format
  // Matches: /manage-programs/product-code/{productCode}/rpn/{R-prefixed-rpnId}/fpn/{F-prefixed-fpnId}
  const pathMatch = pathname.match(/\/manage-programs\/product-code\/([^/]+)(?:\/rpn\/([^/]+))?(?:\/fpn\/([^/]+))?/);

  const productCode = pathMatch?.[1] ?? null;
  const rpnDisplayId = pathMatch?.[2] ?? null; // e.g., 'R11243'
  const fpnDisplayId = pathMatch?.[3] ?? null; // e.g., 'F010652'

  // Get product line by product_code
  const productLineHierarchyMetadata = productCode
    ? (productHierarchyData.find((pl) => pl.product_code === productCode) ?? null)
    : null;

  // Get product line ID from the hierarchy metadata
  const productLineId = productLineHierarchyMetadata?.product_line_id ?? null;

  // Find RPN by display ID in minimal hierarchy
  const rpnHierarchyMetadata =
    productLineHierarchyMetadata && rpnDisplayId
      ? (productLineHierarchyMetadata.children?.find((r) => r.rpn_display_id === rpnDisplayId) ?? null)
      : null;

  // Get RPN ID from the found RPN
  const rpnId = rpnHierarchyMetadata?.rpn_id ?? null;

  const rpnName = rpnHierarchyMetadata?.rpn_alias ?? '';

  // Find FPN by display ID in minimal hierarchy
  const fpnHierarchyMetadata =
    rpnHierarchyMetadata && fpnDisplayId
      ? (rpnHierarchyMetadata.children?.find((f) => f.fpn_display_id === fpnDisplayId) ?? null)
      : null;

  // Get FPN ID from the found FPN
  const fpnId = fpnHierarchyMetadata?.fpn_id ?? null;

  const fpnName = fpnHierarchyMetadata?.fpn_alias ?? '';

  return {
    productLineId,
    rpnId,
    rpnDisplayId,
    rpnName,
    fpnId,
    fpnDisplayId,
    fpnName,
    productLineHierarchyMetadata,
    rpnHierarchyMetadata,
    fpnHierarchyMetadata,
  };
};
